/**
 * Bulk Upload Manager Component
 * Displays upload queue with individual progress bars and controls
 */
import React, { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  List,
  Progress,
  Button,
  Space,
  Typography,
  Tag,
  Tooltip,
  Badge,
  Collapse,
} from 'antd';
import {
  CloseOutlined,
  ReloadOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  DeleteOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import {
  removeFromQueue,
  clearCompleted,
  retryUpload,
  uploadFileWithProgress,
  initiateChunkedUpload,
  uploadChunk,
  completeChunkedUpload,
  setUploadStatus,
  incrementActiveUploads,
  decrementActiveUploads,
  setProcessing,
  saveSession,
} from '../store/uploadQueueSlice';
import { fetchDocuments } from '../store/documentsSlice';

const { Text, Title } = Typography;
const { Panel } = Collapse;

const CHUNK_SIZE = 5 * 1024 * 1024;

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getStatusTag(status) {
  const statusConfig = {
    queued: { color: 'default', text: 'Queued' },
    uploading: { color: 'processing', text: 'Uploading' },
    processing: { color: 'processing', text: 'Processing' },
    completed: { color: 'success', text: 'Completed' },
    failed: { color: 'error', text: 'Failed' },
    paused: { color: 'warning', text: 'Paused' },
  };
  const config = statusConfig[status] || { color: 'default', text: status };
  return <Tag color={config.color}>{config.text}</Tag>;
}

function BulkUploadManager({ visible = true }) {
  const dispatch = useDispatch();
  const { queue, activeUploads, maxConcurrent, completedCount, failedCount, isProcessing } =
    useSelector((state) => state.uploadQueue);
  const processingRef = useRef(false);
  const uploadInProgressRef = useRef(new Set());

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    dispatch(setProcessing(true));

    while (true) {
      const pendingItems = queue.filter(
        (item) =>
          item.status === 'queued' && !uploadInProgressRef.current.has(item.id)
      );
      const currentActive = uploadInProgressRef.current.size;

      if (pendingItems.length === 0 || currentActive >= maxConcurrent) {
        break;
      }

      const slotsAvailable = maxConcurrent - currentActive;
      const itemsToProcess = pendingItems.slice(0, slotsAvailable);

      for (const item of itemsToProcess) {
        uploadInProgressRef.current.add(item.id);
        dispatch(incrementActiveUploads());

        processUpload(item).finally(() => {
          uploadInProgressRef.current.delete(item.id);
          dispatch(decrementActiveUploads());
          setTimeout(() => processQueue(), 100);
        });
      }
      break;
    }

    processingRef.current = false;
    dispatch(setProcessing(false));
  }, [queue, maxConcurrent, dispatch]);

  const processUpload = async (item) => {
    const file = item.fileObject;

    if (!file) {
      dispatch(
        setUploadStatus({
          uploadId: item.id,
          status: 'failed',
          error: 'File object not available',
        })
      );
      return;
    }

    try {
      if (item.isChunked) {
        await processChunkedUpload(item, file);
      } else {
        await dispatch(
          uploadFileWithProgress({
            file,
            folderId: item.folderId,
            uploadId: item.id,
          })
        ).unwrap();
      }
      dispatch(fetchDocuments({}));
    } catch (error) {
      console.error('Upload failed:', error);
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || error?.error || 'Upload failed';
      dispatch(
        setUploadStatus({
          uploadId: item.id,
          status: 'failed',
          error: errorMessage,
        })
      );
    }
  };

  const processChunkedUpload = async (item, file) => {
    const result = await dispatch(
      initiateChunkedUpload({
        file,
        folderId: item.folderId,
        uploadId: item.id,
      })
    ).unwrap();

    const sessionId = result.session.session_id;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    dispatch(
      saveSession({
        uploadId: item.id,
        sessionId,
        filename: file.name,
        uploadedChunks: 0,
        totalChunks,
      })
    );

    for (let i = 0; i < totalChunks; i++) {
      await dispatch(
        uploadChunk({
          file,
          sessionId,
          chunkIndex: i,
          uploadId: item.id,
          totalChunks,
        })
      ).unwrap();
    }

    await dispatch(
      completeChunkedUpload({
        sessionId,
        uploadId: item.id,
      })
    ).unwrap();
  };

  useEffect(() => {
    const queuedItems = queue.filter((item) => item.status === 'queued');
    if (queuedItems.length > 0 && !processingRef.current) {
      processQueue();
    }
  }, [queue, processQueue]);

  const handleRemove = (uploadId) => {
    dispatch(removeFromQueue(uploadId));
  };

  const handleRetry = (uploadId) => {
    dispatch(retryUpload(uploadId));
  };

  const handleClearCompleted = () => {
    dispatch(clearCompleted());
  };

  if (!visible || queue.length === 0) {
    return null;
  }

  const activeCount = queue.filter(
    (item) => item.status === 'uploading' || item.status === 'processing'
  ).length;
  const queuedCount = queue.filter((item) => item.status === 'queued').length;

  return (
    <Card
      size="small"
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: 400,
        maxHeight: 400,
        overflow: 'auto',
        zIndex: 1000,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={5} style={{ margin: 0 }}>
            <Badge count={queue.length} offset={[10, 0]}>
              Upload Queue
            </Badge>
          </Title>
          <Space size="small">
            {(completedCount > 0 || failedCount > 0) && (
              <Tooltip title="Clear completed">
                <Button
                  size="small"
                  icon={<ClearOutlined />}
                  onClick={handleClearCompleted}
                />
              </Tooltip>
            )}
          </Space>
        </Space>
        <Space size="small" style={{ marginTop: 8 }}>
          {queuedCount > 0 && <Tag>Queued: {queuedCount}</Tag>}
          {activeCount > 0 && <Tag color="processing">Active: {activeCount}</Tag>}
          {completedCount > 0 && <Tag color="success">Done: {completedCount}</Tag>}
          {failedCount > 0 && <Tag color="error">Failed: {failedCount}</Tag>}
        </Space>
      </div>

      <List
        size="small"
        dataSource={queue}
        renderItem={(item) => (
          <List.Item
            key={item.id}
            style={{
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <div style={{ width: '100%' }}>
              <Space
                style={{ width: '100%', justifyContent: 'space-between' }}
              >
                <Space>
                  <FileOutlined />
                  <div>
                    <Text
                      ellipsis
                      style={{ maxWidth: 200, display: 'block' }}
                      title={item.file.name}
                    >
                      {item.file.name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {formatFileSize(item.file.size)}
                      {item.isChunked && ' (chunked)'}
                    </Text>
                  </div>
                </Space>
                <Space size="small">
                  {getStatusTag(item.status)}
                  {item.status === 'failed' && (
                    <Tooltip title="Retry">
                      <Button
                        size="small"
                        type="text"
                        icon={<ReloadOutlined />}
                        onClick={() => handleRetry(item.id)}
                      />
                    </Tooltip>
                  )}
                  {(item.status === 'queued' ||
                    item.status === 'failed' ||
                    item.status === 'completed') && (
                    <Tooltip title="Remove">
                      <Button
                        size="small"
                        type="text"
                        danger
                        icon={<CloseOutlined />}
                        onClick={() => handleRemove(item.id)}
                      />
                    </Tooltip>
                  )}
                </Space>
              </Space>
              {(item.status === 'uploading' || item.status === 'processing') && (
                <Progress
                  percent={item.progress}
                  size="small"
                  status={item.status === 'processing' ? 'active' : 'normal'}
                  style={{ marginTop: 4 }}
                />
              )}
              {item.error && (
                <Text type="danger" style={{ fontSize: 11, display: 'block' }}>
                  {item.error}
                </Text>
              )}
            </div>
          </List.Item>
        )}
      />
    </Card>
  );
}

export default BulkUploadManager;
